import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { combineLatest, filter, map, merge, Observable } from 'rxjs';

import { AlooraHeaderService } from '@core/aloora-title/aloora-header.service';
import { Permission } from '@core/api/model';
import { User } from '@core/api/model/User';
import { AuthService } from '@core/auth-service/auth.service';
import { LoadingService } from '@core/loading.service';

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./toolbar.component.scss'],
  standalone: false,
})
export class ToolbarComponent implements OnInit {
  @Output() sideMenuOpen = new EventEmitter<boolean>();

  private readonly alooraHeaderService = inject(AlooraHeaderService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);

  public title: Observable<string>;
  public currentUser$: Observable<User | null> = this.authService.loggedInUser$;
  public userIsLoggedIn$: Observable<boolean> = this.authService.userIsLoggedIn$;
  public pageIsReloadable$!: Observable<boolean>;
  public newOrderButtonVisible: Observable<boolean> = combineLatest([
    this.currentUser$,
    this.alooraHeaderService.newOrderButtonHidden,
  ]).pipe(map(([user, hidden]) => !!user?.hasPermission(Permission.ORDERS) && !hidden));
  public showTutorialButton$!: Observable<boolean>;

  constructor() {
    const alooraHeaderService = this.alooraHeaderService;

    this.title = alooraHeaderService.getTitle();
  }

  ngOnInit(): void {
    this.pageIsReloadable$ = this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.hasReloadableComponent(this.router.routerState.snapshot.root)),
    );
    const mainPageHasTutorial$ = this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.hasTutorial(this.router.routerState.snapshot.root)),
    );
    this.showTutorialButton$ = merge(mainPageHasTutorial$, this.alooraHeaderService.showTutorial$);

    // reset to default value after route changes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.alooraHeaderService.newOrderButtonHidden.next(false));
  }

  public openSideMenu(): void {
    this.sideMenuOpen.emit(true);
  }

  public reloadPage(): void {
    this.loadingService.reloadPage();
  }
  public startTutorial() {
    this.alooraHeaderService.startTutorial();
  }
  private hasReloadableComponent(snapshot: ActivatedRouteSnapshot) {
    if (snapshot.data.supportsReloading) {
      return true;
    }
    for (const child of snapshot.children) {
      if (this.hasReloadableComponent(child)) {
        return true;
      }
    }
    return false;
  }
  private hasTutorial(snapshot: ActivatedRouteSnapshot) {
    if (snapshot.data.hasTutorial) {
      return true;
    }
    for (const child of snapshot.children) {
      if (this.hasTutorial(child)) {
        return true;
      }
    }
    return false;
  }
}
